import Vue from 'vue';
import {getThreads, getThread} from '@/utils/api';
import {getPastThread} from '@/utils/messages';
import merge from 'deepmerge';

const moment = require('moment');

const state = {
  charities: {},
};

// getters
const getters = {
  getThreads: (state) => (charity_id) => {
    if (charity_id in state.charities) {
      return state.charities[charity_id]['threads'];
    }

    return [];
  },
  getUnreadCount: (state) => (charity_id) => {
    let result = {};
    if (charity_id in state.charities && 'messages' in state.charities[charity_id]) {

      Object.entries(state.charities[charity_id]['messages']).forEach(([uid, msgArray]) => {
        result[uid] = msgArray.filter(msg => !msg.is_read).length;
      });
    }

    return result;
  },
  getMessagesByThread: (state) => (uid, charity_id) => {

    if (charity_id in state.charities && 'messages' in state.charities[charity_id]) {
      if (uid in state.charities[charity_id]['messages']) {
        return state.charities[charity_id]['messages'][uid];
      }
    }

    return [];
  },
  getLastMessages: (state) => (charity_id, limit = 4) => {
    if (charity_id in state.charities && 'messages' in state.charities[charity_id]) {
      let allMessages = [];

      Object.entries(state.charities[charity_id]['messages']).forEach(([uid, msgArray]) => {

        allMessages = allMessages.concat(
          msgArray.filter(msg => msg.direction && msg.direction === 'inbound').
            slice(0, limit).
            map(msg => ({...msg, uid})));
      });

      allMessages = allMessages.sort((a, b) => {
        let t1 = moment(a.timestamp);
        let t2 = moment(b.timestamp);

        if (t1 > t2) {
          return -1;
        } else if (t1 < t2) {
          return 1;
        }
        return 0;
      });

      return allMessages.slice(0, limit);
    }
    return [];
  },
};

// actions
const actions = {

  newMessage({dispatch, state, commit}, {message, charity_id}) {

    //if messages is not exist full update messages
    if (!(charity_id in state.charities)
      || !('messages' in state.charities[charity_id])
      || !(message.uid in state.charities[charity_id]['messages'])
    ) {

      if (!(message.uid in state.charities[charity_id]['messages'])) {
        dispatch('updateThreads', {
          charity_id,
        }).then(() => {
          commit('addMessage', {
            message: message,
            charity_id: charity_id,
          });

          commit('setThreadParams', {
              uid: message.uid,
              charity_id: charity_id,
              params: {
                last_message: message,
                timestamp: message.timestamp,
              },
            },
          );
        });
      }
      else {
        //check if the new tread
        dispatch('updateThread', {
          uid: message.uid,
          charity_id,
        });
      }
    }
    else {
      //else:
      // 1. add new message

      commit('addMessage', {
        message: message,
        charity_id: charity_id,
      });

      // 2. update last message in a thread
      commit('setThreadParams', {
          uid: message.uid,
          charity_id: charity_id,
          params: {
            last_message: message,
            timestamp: message.timestamp,
          },
        },
      );
    }
  },
  updateThreadIfEmpty({dispatch, state}, {uid, charity_id}) {
    if (!(charity_id in state.charities)
      || !('messages' in state.charities[charity_id])
      || !(uid in state.charities[charity_id]['messages'])
    ) {
      dispatch('updateThread', {uid, charity_id});
    }
  },
  updateThread({commit}, {uid, charity_id}) {

    getThread(uid).then(res => {
      commit('putMessages', {
          messages: res.messages,
          uid: uid,
          charity_id: charity_id,
        },
      );

      if (res.messages.length) {
        commit('setThreadParams', {
            uid: uid,
            charity_id: charity_id,
            params: {
              last_message: res.messages[0],
              timestamp: res.messages[0].timestamp,
            },
          },
        );
      }
    });
  },
  updateThreads({commit, state, dispatch}, {charity_id}) {
    return new Promise((resolve) => {
      if (!(charity_id in state.charities)) {
        commit('setCharity', {
            data: {'threads': [], messages: {}},
            charity_id: charity_id,
          },
        );
      }
      getThreads().then(res => {
        //remove duplicates
        const threads = res.threads.reduce((ret, val) => {
          let exist = false;
          for (let i = 0; i < ret.length; i++) {
            if (ret[i].uid === val.uid) {
              exist = true;
              break;
            }
          }
          if (!exist) {
            ret.push(val);
          }
          return ret;
        }, []);

        if (state.charities[charity_id].threads.length === 0) {
          //initial only
          commit('setThreads', {threads, charity_id});
        }
        else {

          for (let i = 0; i < threads.length; i++) {
            const thread = threads[i];

            let lastViewed = getPastThread(thread.uid, state.charities[charity_id].threads);

            if (!lastViewed || !lastViewed.last_message) {
              //thread exist but new message added
              commit('setThreads', {threads, charity_id});
              commit('putMessages', {
                  messages: [],
                  uid: thread.uid,
                  charity_id: charity_id,
                },
              );
            }
            else if (parseInt(lastViewed.last_message.id) !== parseInt(thread.last_message.id)) {
              dispatch('updateThread', {uid: thread.uid, charity_id});
            }

            if(lastViewed && lastViewed.notifications_enabled !== thread.notifications_enabled){
              commit('setThreadParams', {
                  uid: thread.uid,
                  charity_id: charity_id,
                  params: {
                    notifications_enabled: thread.notifications_enabled
                  },
                },
              );

            }
          }
        }

        resolve(threads);
      });
    });
  },
};

// mutations
const mutations = {
  /**
   * set data for all threads (initial)
   * @param state
   * @param threads
   * @param charity_id
   */
  setThreads(state, {threads, charity_id}) {
    Vue.set(state.charities[charity_id], 'threads', threads);
  },
  /**
   * updated params for one thread
   * @param state
   * @param uid
   * @param charity_id
   * @param params
   */
  setThreadParams(state, {uid, charity_id, params}) {
    const index = state.charities[charity_id].threads.map(e => parseInt(e.uid)).indexOf(parseInt(uid));

    if (index !== -1) {
      Vue.set(
        state.charities[charity_id].threads,
        index,
        merge(state.charities[charity_id].threads[index], params),
      );
    }
  },
  /**
   * set charity params (messages, threads) (initial)
   * @param state
   * @param data
   * @param charity_id
   */
  setCharity(state, {data, charity_id}) {
    Vue.set(state.charities, charity_id, data);
  },
  /**
   * add new message to thread
   * @param state
   * @param getters
   * @param message
   * @param charity_id
   */
  addMessage(state, {message, charity_id}) {

    let messages = [];
    if (message.uid in state.charities[charity_id]['messages']) {
      messages = state.charities[charity_id]['messages'][message.uid];
    }
    messages.unshift(message);

    Vue.set(state.charities[charity_id]['messages'], message.uid, messages);
  },
  /**
   * replace messages array by thread (only read status will copied from old array)
   * read status:
   * - if initial call - get last message from thread
   * - else copy read status form current array
   *
   * @param state
   * @param messages
   * @param uid
   * @param charity_id
   */
  putMessages(state, {messages, uid, charity_id}) {
    if (uid in state.charities[charity_id]['messages']) {
      //copy read status
      const lastReadMsgId = parseInt(state.charities[charity_id]['messages'][uid].reduce(
        (res, msg) => msg.is_read && res < msg.id ? msg.id : res, 0));

      messages = messages.map(msg => ({...msg, is_read: parseInt(msg.id) <= lastReadMsgId}));
    }
    else {
      //init call

      const index = state.charities[charity_id].threads.map(e => parseInt(e.uid)).indexOf(parseInt(uid));
      if (index !== -1) {
        const lastReadMsgId = parseInt(state.charities[charity_id].threads[index].last_message.id);

        messages = messages.map(msg => ({...msg, is_read: parseInt(msg.id) <= lastReadMsgId}));

      } else {
        messages = messages.map(msg => ({...msg, is_read: true}));
      }
    }
    Vue.set(state.charities[charity_id]['messages'], uid, messages);
  },
  /**
   * mark thread as read
   * @param state
   * @param uid
   * @param charity_id
   */
  markAsRead(state, {uid, charity_id}) {
    if (uid in state.charities[charity_id]['messages']) {

      Vue.set(state.charities[charity_id]['messages'], uid,
        state.charities[charity_id]['messages'][uid].map(msg => ({...msg, is_read: true})));
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
